(function () {
  class PreviewSlideZoom {
    constructor() {
      this.items = [];
      this.index = 0;
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.drag = null;
      this.swipe = null;
      this.modal = null;
      this.itemsKey = '';
      this.renderRaf = 0;
      this.zoomMediaRaf = 0;
      this.imageAnimationTimer = 0;
      this.openAnimationTimer = 0;
      this.productTitleObserver = null;
      this.zoomMediaObserver = null;
      this.transitionDirection = 'next';
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundClick = this.onDocumentClick.bind(this);
      this.boundWheel = this.onWheel.bind(this);
      this.init();
    }

    init() {
      this.lockProductTitle();
      document.addEventListener('click', this.boundClick, true);
      document.addEventListener('shopify:section:load', () => {
        this.close();
        this.lockProductTitle();
        this.observeProductChanges();
      });
      this.guardPwwpTouchNavigation();
      this.observeProductChanges();
    }

    guardPwwpTouchNavigation() {
      let lastArrowTouch = null;
      const arrowSelector = '.pwwp__button--arrow--left, .pwwp__button--arrow--right';
      const getPwwpArrow = (target) => target?.closest?.(arrowSelector);

      document.addEventListener(
        'touchend',
        (event) => {
          const arrow = getPwwpArrow(event.target);
          if (!arrow || !arrow.closest('.pwwp--open')) return;
          lastArrowTouch = {
            at: Date.now(),
            arrow,
          };
        },
        true
      );

      document.addEventListener(
        'click',
        (event) => {
          const arrow = getPwwpArrow(event.target);
          if (!arrow || !lastArrowTouch || lastArrowTouch.arrow !== arrow) return;
          if (Date.now() - lastArrowTouch.at > 700) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          lastArrowTouch = null;
        },
        true
      );
    }

    observeProductChanges() {
      this.productTitleObserver?.disconnect();

      const titleWrapper = document.querySelector('product-info .product__title');
      if (!titleWrapper) return;

      this.productTitleObserver = new MutationObserver(() => {
        this.restoreProductTitle();
      });
      this.productTitleObserver.observe(titleWrapper, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    observeOpenProductMedia() {
      this.disconnectOpenProductMediaObserver();

      const mediaWrapper = document.querySelector('.product__media-wrapper');
      if (!mediaWrapper) return;

      this.zoomMediaObserver = new MutationObserver(() => this.scheduleZoomItemsRefresh());
      this.zoomMediaObserver.observe(mediaWrapper, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true
      });
    }

    disconnectOpenProductMediaObserver() {
      this.zoomMediaObserver?.disconnect();
      this.zoomMediaObserver = null;
      if (this.zoomMediaRaf) cancelAnimationFrame(this.zoomMediaRaf);
      this.zoomMediaRaf = 0;
    }

    scheduleZoomItemsRefresh() {
      if (!this.modal?.classList.contains('is-open')) return;

      if (this.zoomMediaRaf) cancelAnimationFrame(this.zoomMediaRaf);
      this.zoomMediaRaf = requestAnimationFrame(() => {
        this.zoomMediaRaf = 0;
        this.refreshZoomItemsFromProduct();
      });
    }

    refreshZoomItemsFromProduct() {
      if (!this.modal?.classList.contains('is-open')) return;

      const activeMediaId = this.items[this.index]?.mediaId;
      const liveGallery = Array.from(document.querySelectorAll('.product__media-wrapper > #tee-gallery.tee-gallery-content')).find((gallery) =>
        this.isVisible(gallery)
      );
      const nextItems = liveGallery ? this.collectTeeItems(liveGallery) : this.collectItems();
      const nextItemsKey = this.createItemsKey(nextItems);
      if (nextItemsKey !== this.itemsKey) {
        this.items = nextItems;
        this.itemsKey = nextItemsKey;
        this.renderThumbs();
        this.preloadAround(this.index);
      }
      const nextIndex = this.items.findIndex((item) => item.mediaId === activeMediaId);
      this.index = nextIndex >= 0 ? nextIndex : Math.min(this.index, this.items.length - 1);
      this.scheduleRender();
    }

    lockProductTitle() {
      const wrapper = document.querySelector('product-info .product__title');
      const title = wrapper?.querySelector('h1');
      if (!title) return;
      this.originalProductTitle = title.textContent.trim();
      this.originalProductTitleState = {
        wrapperClass: wrapper.className,
        wrapperStyle: wrapper.getAttribute('style'),
        titleClass: title.className,
        titleStyle: title.getAttribute('style'),
      };
      title.dataset.tbOriginalTitle = this.originalProductTitle;
    }

    restoreProductTitle() {
      if (!this.originalProductTitle) return;
      const wrapper = document.querySelector('product-info .product__title');
      const state = this.originalProductTitleState;

      if (wrapper && state) {
        if (wrapper.className !== state.wrapperClass) wrapper.className = state.wrapperClass;
        if (state.wrapperStyle === null) wrapper.removeAttribute('style');
        else if (wrapper.getAttribute('style') !== state.wrapperStyle) wrapper.setAttribute('style', state.wrapperStyle);
      }

      document.querySelectorAll('product-info .product__title h1, product-info .product__title h2').forEach((title) => {
        if (title.textContent.trim() !== this.originalProductTitle) {
          title.textContent = this.originalProductTitle;
        }

        if (!state) return;
        if (title.tagName === 'H1' && title.className !== state.titleClass) title.className = state.titleClass;
        if (title.tagName === 'H1') {
          if (state.titleStyle === null) title.removeAttribute('style');
          else if (title.getAttribute('style') !== state.titleStyle) title.setAttribute('style', state.titleStyle);
        }
      });
    }

    onDocumentClick(event) {
      const mediaWrapper = event.target.closest('.product__media-wrapper');
      const liveGallery = mediaWrapper?.querySelector(':scope > #tee-gallery.tee-gallery-content');
      if (liveGallery && this.isVisible(liveGallery) && !event.target.closest('.tb-preview-zoom')) {
        const items = this.collectTeeItems(liveGallery);
        if (items.length) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.open(items, 0);
        }
        return;
      }

      const teeTrigger = event.target.closest(
        '#tee-gallery img, #tee-gallery canvas, #tee-gallery .tee-slide, #tee-gallery .tee-mockup-zoomable, #tee-gallery .tee-mockup'
      );
      if (teeTrigger && !teeTrigger.closest('.tb-preview-zoom')) {
        const teeGallery = teeTrigger.closest('#tee-gallery');
        const items = this.collectTeeItems(teeGallery);
        if (!items.length) return;

        const slide = teeTrigger.closest('.tee-slide, .tee-mockup-item, .tee-mockup');
        const slideIndex = slide ? Array.from(teeGallery.querySelectorAll('.tee-slide, .tee-mockup-item, .tee-mockup')).indexOf(slide) : 0;
        const startIndex = Math.max(0, Math.min(slideIndex, items.length - 1));

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.open(items, startIndex);
        return;
      }

      const trigger = event.target.closest('.product__media-toggle, .product__modal-opener--image');
      if (!trigger || trigger.closest('.tb-preview-zoom')) return;

      const gallery = trigger.closest('.product__media-wrapper')?.querySelector('media-gallery');
      if (!gallery) return;

      const items = this.collectItems(gallery);
      if (!items.length) return;

      const mediaId =
        trigger.dataset.mediaId ||
        trigger.querySelector('[data-media-id]')?.getAttribute('data-media-id') ||
        trigger.closest('[data-media-id]')?.getAttribute('data-media-id');
      const startIndex = Math.max(
        0,
        items.findIndex((item) => item.mediaId === mediaId)
      );

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.open(items, startIndex);
    }

    collectItems(gallery = document.querySelector('.product__media-wrapper media-gallery')) {
      if (!gallery) return [];

      const slides = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]'));
      const items = slides
        .map((slide) => {
          const image = slide.querySelector('.product__modal-opener--image img, .product__media img');
          if (!image) return null;

          const mediaId = slide.dataset.mediaId;
          const thumbImage = gallery.querySelector(`[data-target="${mediaId}"] img`);
          const fullSrc = this.bestImageSource(image);
          const thumbSrc = this.bestImageSource(thumbImage || image);

          return {
            mediaId,
            src: fullSrc,
            thumb: thumbSrc,
            alt: image.alt || '',
          };
        })
        .filter(Boolean);

      return items.filter((item, index, list) => list.findIndex((candidate) => candidate.mediaId === item.mediaId) === index);
    }

    collectTeeItems(gallery) {
      if (!gallery) return [];

      const mainImages = Array.from(gallery.querySelectorAll('img, canvas')).filter((node) => {
        if (node.closest('.tee-thumbnails')) return false;
        if (node.classList.contains('tee-image--loader')) return false;
        return this.isVisible(node);
      });

      const fallbackImages = Array.from(gallery.querySelectorAll('img, canvas')).filter((node) => {
        if (node.classList.contains('tee-image--loader')) return false;
        return this.isVisible(node);
      });

      const nodes = mainImages.length ? mainImages : fallbackImages;
      const thumbs = Array.from(gallery.querySelectorAll('.tee-thumbnails img, .tee-thumbnails canvas'));
      const items = nodes
        .map((node, index) => {
          const src = this.nodeSource(node);
          if (!src) return null;
          const thumb = this.nodeSource(thumbs[index]) || src;
          return {
            mediaId: `tee-${index}`,
            src,
            thumb,
            alt: node.alt || 'Personalized product preview',
          };
        })
        .filter(Boolean);

      return items.filter((item, index, list) => list.findIndex((candidate) => candidate.src === item.src) === index);
    }

    isVisible(element) {
      if (!element) return false;
      const style = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    }

    nodeSource(node) {
      if (!node) return '';
      if (node.tagName === 'CANVAS') {
        try {
          return node.toDataURL('image/png');
        } catch (error) {
          return '';
        }
      }
      return this.bestImageSource(node);
    }

    bestImageSource(image) {
      if (!image) return '';
      if (image.currentSrc) return image.currentSrc;
      if (image.src) return image.src;
      const srcset = image.getAttribute('srcset');
      if (!srcset) return '';
      const last = srcset
        .split(',')
        .map((entry) => entry.trim().split(/\s+/)[0])
        .filter(Boolean)
        .pop();
      return last || '';
    }

    ensureModal() {
      if (this.modal) return;

      const modal = document.createElement('div');
      modal.className = 'tb-preview-zoom';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Product image preview');
      modal.innerHTML = `
        <div class="tb-preview-zoom__overlay" data-tb-close></div>
        <div class="tb-preview-zoom__shell">
          <div class="tb-preview-zoom__viewport" data-tb-viewport>
            <button class="tb-preview-zoom__button tb-preview-zoom__close" type="button" aria-label="Close preview" data-tb-close>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.4 5 5 6.4l5.6 5.6L5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4 17.6 5 12 10.6 6.4 5z" fill="currentColor"/></svg>
            </button>
            <button class="tb-preview-zoom__button tb-preview-zoom__prev" type="button" aria-label="Previous image" data-tb-prev>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15.4 4.6 1.4 1.4-6 6 6 6-1.4 1.4L8 12l7.4-7.4z" fill="currentColor"/></svg>
            </button>
            <div class="tb-preview-zoom__stage">
              <img class="tb-preview-zoom__image" alt="" draggable="false">
            </div>
            <button class="tb-preview-zoom__button tb-preview-zoom__next" type="button" aria-label="Next image" data-tb-next>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.6 19.4 7.2 18l6-6-6-6 1.4-1.4L16 12l-7.4 7.4z" fill="currentColor"/></svg>
            </button>
            <div class="tb-preview-zoom__tools" aria-label="Zoom controls">
              <button class="tb-preview-zoom__button tb-preview-zoom__tool" type="button" aria-label="Zoom out" data-tb-zoom-out>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 11h14v2H5v-2z" fill="currentColor"/></svg>
              </button>
              <span class="tb-preview-zoom__counter" data-tb-counter>1 / 1</span>
              <button class="tb-preview-zoom__button tb-preview-zoom__tool" type="button" aria-label="Zoom in" data-tb-zoom-in>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" fill="currentColor"/></svg>
              </button>
              <button class="tb-preview-zoom__button tb-preview-zoom__tool" type="button" aria-label="Reset zoom" data-tb-reset>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5a7 7 0 1 1-6.32 4H3.5A9 9 0 1 0 12 3v2zm-4.5.5H3v4.5h2V8.9A7 7 0 0 1 12 5V3a8.98 8.98 0 0 0-4.5 1.2v1.3z" fill="currentColor"/></svg>
              </button>
            </div>
          </div>
          <div class="tb-preview-zoom__thumbs" data-tb-thumbs></div>
        </div>
      `;

      this.modal = modal;
      this.viewport = modal.querySelector('[data-tb-viewport]');
      this.image = modal.querySelector('.tb-preview-zoom__image');
      this.thumbs = modal.querySelector('[data-tb-thumbs]');
      this.counter = modal.querySelector('[data-tb-counter]');
      this.prevButton = modal.querySelector('[data-tb-prev]');
      this.nextButton = modal.querySelector('[data-tb-next]');
      this.zoomInButton = modal.querySelector('[data-tb-zoom-in]');
      this.zoomOutButton = modal.querySelector('[data-tb-zoom-out]');
      this.resetButton = modal.querySelector('[data-tb-reset]');

      modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-tb-close]')) this.close();
        if (event.target.closest('[data-tb-prev]')) this.previous();
        if (event.target.closest('[data-tb-next]')) this.next();
        if (event.target.closest('[data-tb-zoom-in]')) this.zoomBy(0.5);
        if (event.target.closest('[data-tb-zoom-out]')) this.zoomBy(-0.5);
        if (event.target.closest('[data-tb-reset]')) this.resetZoom();
      });

      this.image.addEventListener('dblclick', () => {
        if (this.scale > 1) this.resetZoom();
        else this.setZoom(2);
      });

      this.viewport.addEventListener('wheel', this.boundWheel, { passive: false });
      this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));

      document.body.appendChild(modal);
    }

    open(items, index = 0) {
      this.ensureModal();
      this.items = items;
      this.itemsKey = this.createItemsKey(items);
      this.index = index;
      this.resetZoom();
      this.renderThumbs();
      this.render();
      this.preloadAround(index);
      this.modal.classList.add('is-open');
      this.playOpenAnimation();
      document.body.classList.add('tb-preview-zoom-open');
      document.addEventListener('keydown', this.boundKeydown);
      this.observeOpenProductMedia();
      this.modal.querySelector('[data-tb-close]').focus({ preventScroll: true });
    }

    close() {
      if (!this.modal?.classList.contains('is-open')) return;
      clearTimeout(this.imageAnimationTimer);
      clearTimeout(this.openAnimationTimer);
      this.modal.classList.remove('is-open', 'is-opening', 'is-zoomed', 'is-dragging', 'is-changing', 'is-changing-next', 'is-changing-prev');
      document.body.classList.remove('tb-preview-zoom-open');
      document.removeEventListener('keydown', this.boundKeydown);
      this.disconnectOpenProductMediaObserver();
      this.items = [];
      this.itemsKey = '';
      this.drag = null;
      this.swipe = null;
      if (this.renderRaf) cancelAnimationFrame(this.renderRaf);
      this.renderRaf = 0;
      this.resetZoom();
    }

    createItemsKey(items) {
      return items.map((item) => `${item.mediaId}:${item.src}`).join('|');
    }

    scheduleRender() {
      if (this.renderRaf) cancelAnimationFrame(this.renderRaf);
      this.renderRaf = requestAnimationFrame(() => {
        this.renderRaf = 0;
        this.render();
      });
    }

    render() {
      if (!this.items.length) return this.close();

      const item = this.items[this.index];
      const srcChanged = this.image.src !== item.src;
      if (srcChanged) {
        this.image.src = item.src;
        this.playImageChangeAnimation(this.transitionDirection);
      }
      this.image.alt = item.alt;
      this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
      this.prevButton.disabled = this.items.length < 2;
      this.nextButton.disabled = this.items.length < 2;
      this.thumbs.querySelectorAll('[data-tb-index]').forEach((button) => {
        button.classList.toggle('is-active', Number(button.dataset.tbIndex) === this.index);
      });
      this.applyTransform();
    }

    renderThumbs() {
      this.thumbs.innerHTML = this.items
        .map(
          (thumb, index) => `
            <button class="tb-preview-zoom__thumb${index === this.index ? ' is-active' : ''}" type="button" data-tb-index="${index}" aria-label="View image ${index + 1}">
              <img src="${thumb.thumb}" alt="">
            </button>
          `
        )
        .join('');

      this.thumbs.querySelectorAll('[data-tb-index]').forEach((button) => {
        button.addEventListener('click', () => this.goTo(Number(button.dataset.tbIndex)));
      });
    }

    preloadAround(index) {
      if (!this.items.length) return;
      [index, index + 1, index - 1].forEach((rawIndex) => {
        const item = this.items[(rawIndex + this.items.length) % this.items.length];
        if (!item?.src || item.preloaded) return;
        const image = new Image();
        image.decoding = 'async';
        image.src = item.src;
        item.preloaded = true;
      });
    }

    goTo(index, direction) {
      if (!this.items.length) return;
      const nextIndex = (index + this.items.length) % this.items.length;
      if (nextIndex === this.index) return;
      this.transitionDirection = direction || (nextIndex > this.index ? 'next' : 'prev');
      this.index = nextIndex;
      this.resetZoom();
      this.render();
      this.preloadAround(this.index);
    }

    previous() {
      this.goTo(this.index - 1, 'prev');
    }

    next() {
      this.goTo(this.index + 1, 'next');
    }

    playOpenAnimation() {
      if (!this.modal) return;
      this.modal.classList.remove('is-opening');
      this.modal.offsetWidth;
      this.modal.classList.add('is-opening');
      clearTimeout(this.openAnimationTimer);
      this.openAnimationTimer = window.setTimeout(() => {
        this.modal?.classList.remove('is-opening');
      }, 360);
    }

    playImageChangeAnimation(direction = 'next') {
      if (!this.modal?.classList.contains('is-open')) return;
      this.modal.classList.remove('is-changing', 'is-changing-next', 'is-changing-prev');
      this.image.offsetWidth;
      this.modal.classList.add('is-changing', direction === 'prev' ? 'is-changing-prev' : 'is-changing-next');
      clearTimeout(this.imageAnimationTimer);
      this.imageAnimationTimer = window.setTimeout(() => {
        this.modal?.classList.remove('is-changing', 'is-changing-next', 'is-changing-prev');
      }, 280);
    }

    onKeydown(event) {
      if (!this.modal?.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.zoomBy(0.5);
      } else if (event.key === '-') {
        event.preventDefault();
        this.zoomBy(-0.5);
      } else if (event.key === '0') {
        event.preventDefault();
        this.resetZoom();
      }
    }

    onWheel(event) {
      if (!this.modal?.classList.contains('is-open')) return;
      event.preventDefault();
      this.zoomBy(event.deltaY < 0 ? 0.25 : -0.25);
    }

    onPointerDown(event) {
      if (!this.modal?.classList.contains('is-open')) return;
      if (event.target.closest('button')) return;

      this.viewport.setPointerCapture(event.pointerId);
      if (this.scale > 1) {
        this.drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          panX: this.panX,
          panY: this.panY,
        };
        this.modal.classList.add('is-dragging');
      } else {
        this.swipe = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
        };
      }
    }

    onPointerMove(event) {
      if (this.drag?.pointerId === event.pointerId) {
        this.panX = this.drag.panX + event.clientX - this.drag.startX;
        this.panY = this.drag.panY + event.clientY - this.drag.startY;
        this.clampPan();
        this.applyTransform();
      }
    }

    onPointerUp(event) {
      if (this.drag?.pointerId === event.pointerId) {
        this.drag = null;
        this.modal.classList.remove('is-dragging');
        return;
      }

      if (this.swipe?.pointerId === event.pointerId) {
        const deltaX = event.clientX - this.swipe.startX;
        const deltaY = event.clientY - this.swipe.startY;
        this.swipe = null;
        if (Math.abs(deltaX) > 56 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
          if (deltaX < 0) this.next();
          else this.previous();
        }
      }
    }

    zoomBy(delta) {
      this.setZoom(this.scale + delta);
    }

    setZoom(scale) {
      this.scale = Math.min(4, Math.max(1, scale));
      if (this.scale === 1) {
        this.panX = 0;
        this.panY = 0;
      }
      this.clampPan();
      this.applyTransform();
    }

    resetZoom() {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.applyTransform();
    }

    clampPan() {
      if (!this.image || this.scale <= 1) {
        this.panX = 0;
        this.panY = 0;
        return;
      }

      const maxX = (this.image.offsetWidth * (this.scale - 1)) / 2;
      const maxY = (this.image.offsetHeight * (this.scale - 1)) / 2;
      this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
      this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
    }

    applyTransform() {
      if (!this.image) return;
      this.image.style.setProperty('--tb-scale', this.scale);
      this.image.style.setProperty('--tb-pan-x', `${this.panX}px`);
      this.image.style.setProperty('--tb-pan-y', `${this.panY}px`);
      this.modal?.classList.toggle('is-zoomed', this.scale > 1);
      if (this.zoomOutButton) this.zoomOutButton.disabled = this.scale <= 1;
      if (this.zoomInButton) this.zoomInButton.disabled = this.scale >= 4;
    }
  }

  function bootPreviewSlideZoom() {
    if (window.PreviewSlideZoom) return;
    window.PreviewSlideZoom = new PreviewSlideZoom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPreviewSlideZoom);
  } else {
    bootPreviewSlideZoom();
  }
})();
